import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { motion } from "framer-motion";

const LOCATIONS = [
  { name: "New Delhi", lat: 28.6139, lon: 77.2090 },
  { name: "London", lat: 51.5074, lon: -0.1278 },
  { name: "Tokyo", lat: 35.6762, lon: 139.6503 },
  { name: "Reykjavik", lat: 64.1466, lon: -21.9426 },
  { name: "Nairobi", lat: -1.2921, lon: 36.8219 },
];

const WEATHER_CODE = {
  0: "Clear", 1: "Mostly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Foggy", 48: "Rime fog", 51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain", 71: "Light snow", 73: "Snow", 75: "Heavy snow",
  80: "Rain showers", 81: "Heavy showers", 82: "Violent showers",
  95: "Thunderstorm", 96: "Storm w/ hail", 99: "Severe storm",
};

function aqiCategory(v) {
  if (v == null) return { label: "—", cls: "text-[color:var(--np-muted)]" };
  if (v <= 50) return { label: "Good", cls: "text-[color:var(--np-live)]" };
  if (v <= 100) return { label: "Moderate", cls: "text-yellow-300" };
  if (v <= 150) return { label: "Unhealthy (SG)", cls: "text-orange-400" };
  if (v <= 200) return { label: "Unhealthy", cls: "text-[color:var(--np-alert)]" };
  if (v <= 300) return { label: "Very unhealthy", cls: "text-fuchsia-400" };
  return { label: "Hazardous", cls: "text-red-600" };
}

export default function LiveWeatherWidget() {
  const [i, setI] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loc = LOCATIONS[i];

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http.get(`/weather?lat=${loc.lat}&lon=${loc.lon}&name=${encodeURIComponent(loc.name)}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [i, loc.lat, loc.lon, loc.name]);

  const cur = data?.weather?.current;
  const daily = data?.weather?.daily;
  const aqi = data?.air_quality?.current?.us_aqi;
  const cat = aqiCategory(aqi);
  const code = cur?.weather_code;
  const desc = code != null ? WEATHER_CODE[code] || "—" : "—";

  return (
    <motion.div
      data-testid="live-weather-widget"
      className="glass p-5 md:p-6 rounded-sm"
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-text-2)]">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--np-live)] mr-2 align-middle animate-pulse" />
          Live · {loc.name}
        </div>
        <div className="flex gap-1">
          {LOCATIONS.map((_, idx) => (
            <button
              key={idx}
              data-testid={`weather-loc-${idx}`}
              onClick={() => setI(idx)}
              className={`w-6 h-1 transition-colors ${idx === i ? "bg-[color:var(--np-live)]" : "bg-black/20 hover:bg-black/40"}`}
              aria-label={`Show ${LOCATIONS[idx].name}`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Temp</div>
          <div className="font-serif-display text-4xl md:text-5xl leading-none mt-1">
            {loading ? "…" : cur?.temperature_2m != null ? `${Math.round(cur.temperature_2m)}°` : "—"}
          </div>
          <div className="font-mono-tech text-[10px] mt-1 text-[color:var(--np-text-2)] uppercase tracking-wider">{desc}</div>
        </div>
        <div>
          <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">AQI (US)</div>
          <div className={`font-serif-display text-4xl md:text-5xl leading-none mt-1 ${cat.cls}`}>
            {loading ? "…" : (aqi ?? "—")}
          </div>
          <div className={`font-mono-tech text-[10px] mt-1 uppercase tracking-wider ${cat.cls}`}>{cat.label}</div>
        </div>
        <div>
          <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">Wind</div>
          <div className="font-serif-display text-4xl md:text-5xl leading-none mt-1">
            {loading ? "…" : cur?.wind_speed_10m != null ? `${Math.round(cur.wind_speed_10m)}` : "—"}
          </div>
          <div className="font-mono-tech text-[10px] mt-1 text-[color:var(--np-text-2)] uppercase tracking-wider">km/h</div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-black/10 flex items-center justify-between font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[color:var(--np-text-2)]">
        <span>Humidity {cur?.relative_humidity_2m ?? "—"}%</span>
        <span>UV {cur?.uv_index ?? "—"}</span>
        <span>Hi {daily?.temperature_2m_max?.[0] ? Math.round(daily.temperature_2m_max[0]) + "°" : "—"} / Lo {daily?.temperature_2m_min?.[0] ? Math.round(daily.temperature_2m_min[0]) + "°" : "—"}</span>
      </div>
    </motion.div>
  );
}
